import { useLogsStore } from '@/store/logs'

/**
 * Management Class for Syncables.
 */
class SyncableHandler {
  constructor() {
    this.syncables = {}
    this.mainInterval = null
    this.inactivityDetector = null
    this.inactive = false
    this.startMainInterval()
    try {
      if (window) this.startInactivityDetector()
    } catch {}
  }

  /**
   * Checks if a syncable exists for the given global unique identifier and handle.
   * @param syncableClass {Syncable}
   * @param globalUniqueIdentifier {String}
   */
  addSyncable(syncableClass, globalUniqueIdentifier) {
    if (!this.syncables[globalUniqueIdentifier]) {
      this.syncables[globalUniqueIdentifier] = {}
    }
    const handle = syncableClass.getSyncableHandle()
    if (!this.syncables[globalUniqueIdentifier][handle]) {
      this.syncables[globalUniqueIdentifier][handle] = new syncableClass(globalUniqueIdentifier)
    }
    return this.syncables[globalUniqueIdentifier][handle]
  }

  /**
   * Ensures that a syncable exists for the given global unique identifier and handle, and executes it if necessary.
   * @param syncableClass {Syncable}
   * @param globalUniqueIdentifier {String}
   * @returns {Promise<void|*>}
   */
  ensureSyncable(syncableClass, globalUniqueIdentifier) {
    const syncable = this.addSyncable(syncableClass, globalUniqueIdentifier)
    return syncable.maybeExecute()
  }

  /**
   * Executes all syncables, only if the Handler isn't marked as inactive.
   */
  maybeExecuteSyncables() {
    if (this.inactive) return
    for (const globalUniqueIdentifier of Object.keys(this.syncables)) {
      for (const handle of Object.keys(this.syncables[globalUniqueIdentifier])) {
        const syncable = this.syncables[globalUniqueIdentifier][handle]
        syncable.maybeExecute().catch(e => {
          useLogsStore().addLogEntry({
            message: `Syncable ${syncable.handle} failed to execute`,
            error: e,
            tag: 'syncableHandler',
            globalUniqueIdentifier,
            level: 'ERROR',
          })
        })
      }
    }
  }

  /**
   * Starts the main interval for executing syncables.
   */
  startMainInterval() {
    this.mainInterval = setInterval(
      () => {
        this.maybeExecuteSyncables()
      },
      1000 * 5 + Math.floor(Math.random() * 1000)
    )
  }

  /**
   * Resets the inactivity detector.
   * If the handler is currently inactive, it will mark all syncables as active again.
   */
  resetInactivityDetector() {
    if (this.inactive) {
      this.inactive = false
      this.maybeExecuteSyncables()
    }
    if (this.inactivityDetector) clearTimeout(this.inactivityDetector)

    this.inactivityDetector = setTimeout(
      () => {
        useLogsStore().addLogEntry({
          message: 'SyncableHandler detected inactivity, marking all syncables as inactive',
          tag: 'syncableHandler',
          level: 'INFO',
        })
        this.inactive = true
      },
      1000 * 60 * 10
    )
  }

  /**
   * Starts the inactivity detector.
   * This will reset the inactivity detector on various user interactions.
   */
  startInactivityDetector() {
    this.resetInactivityDetector()
    window.onload = this.resetInactivityDetector.bind(this)
    window.onMouseenter = this.resetInactivityDetector.bind(this)
    window.onMouseleave = this.resetInactivityDetector.bind(this)
    window.onmousemove = this.resetInactivityDetector.bind(this)
    window.onmousedown = this.resetInactivityDetector.bind(this)
    window.ontouchstart = this.resetInactivityDetector.bind(this)
    window.onclick = this.resetInactivityDetector.bind(this)
    window.onkeypress = this.resetInactivityDetector.bind(this)
  }
}

export default new SyncableHandler()
